import React, { useRef } from 'react';
import { Form } from 'formik-antd';
import { Formik } from 'formik';
import { Button, Divider, Modal } from 'antd';
import * as Yup from 'yup';

import SubmitButton from 'components/formik/SubmitButton';
import QuestionnaireElement from '../QuestionnaireElement';
import { AssignmentDetailsModel } from 'utils/assignmentUtils';
import {
  AssignmentAdminDocumentModel,
  AssignmentQuestionnaireInfoModel,
} from 'api/adminApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  saveMemberQuestionnaire,
  updateMemberQuestionnaire,
} from 'redux/reducers/assignmentSlice';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  Questionnaire,
  mapQuestionnaireToRequest,
  mapQuestionnaireToInitialValues,
} from '../utils/mapUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  onClose: () => void;
  isAdmin?: boolean;
  isEditMode?: boolean;
  assignmentId: string;
  assignmentKey: string;
  questionnaireInfo?: AssignmentQuestionnaireInfoModel;
  setPrimaryDetails: (data: AssignmentDetailsModel) => void;
  setAdditionalDetails?: (data: AssignmentAdminDocumentModel) => void;
};

interface FormValues {
  requireWorkerTraining: Questionnaire;
  controlWorker: Questionnaire;
  dictateClockInOut: Questionnaire;
  superviseCustomersEmployees: Questionnaire;
  willCustomerProvideSupplies: Questionnaire;
  reimbuseWorkerExpenses: Questionnaire;
  isWorkerNotAvailableForEmployees: Questionnaire;
  areServicesKeyAspectForBusiness: Questionnaire;
  hasPerformedSimilarServices: Questionnaire;
  workerAdvertiseServices: Questionnaire;
}

const elementValidationSchema = Yup.object({
  closedQuestion: Yup.boolean().notRequired(),
  comment: Yup.string()
    .trim()
    .max(2000, 'Comment should be 2000 characters maximum'),
});

const validationSchema = Yup.object({
  requireWorkerTraining: elementValidationSchema,
  controlWorker: elementValidationSchema,
  dictateClockInOut: elementValidationSchema,
  superviseCustomersEmployees: elementValidationSchema,
  willCustomerProvideSupplies: elementValidationSchema,
  reimbuseWorkerExpenses: elementValidationSchema,
  isWorkerNotAvailableForEmployees: elementValidationSchema,
  areServicesKeyAspectForBusiness: elementValidationSchema,
  hasPerformedSimilarServices: elementValidationSchema,
  workerAdvertiseServices: elementValidationSchema,
});

export default function MemberQuestionnaireModal(props: Props) {
  const {
    onClose,
    assignmentId,
    assignmentKey,
    isAdmin,
    setPrimaryDetails,
    setAdditionalDetails,
    questionnaireInfo,
    isEditMode,
  } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const initialValues: FormValues = {
    requireWorkerTraining: mapQuestionnaireToInitialValues(
      questionnaireInfo?.ongoingTrainingRequired,
    ),
    controlWorker: mapQuestionnaireToInitialValues(
      questionnaireInfo?.customerControl,
    ),
    dictateClockInOut: mapQuestionnaireToInitialValues(
      questionnaireInfo?.clockInOrOutDictated,
    ),
    superviseCustomersEmployees: mapQuestionnaireToInitialValues(
      questionnaireInfo?.supervisionOfWorker,
    ),
    willCustomerProvideSupplies: mapQuestionnaireToInitialValues(
      questionnaireInfo?.suppliesProvision,
    ),
    reimbuseWorkerExpenses: mapQuestionnaireToInitialValues(
      questionnaireInfo?.businessExpensesReimbursed,
    ),
    isWorkerNotAvailableForEmployees: mapQuestionnaireToInitialValues(
      questionnaireInfo?.specializedSkillsetEmployeeAvailbility,
    ),
    areServicesKeyAspectForBusiness: mapQuestionnaireToInitialValues(
      questionnaireInfo?.servicesAreKeyAspectOfBM,
    ),
    hasPerformedSimilarServices: mapQuestionnaireToInitialValues(
      questionnaireInfo?.workerPerformedSimilarServices,
    ),
    workerAdvertiseServices: mapQuestionnaireToInitialValues(
      questionnaireInfo?.servicesAdvertisedByWorker,
    ),
  };

  const handleSubmit = async (values: FormValues) => {
    const {
      requireWorkerTraining,
      controlWorker,
      dictateClockInOut,
      superviseCustomersEmployees,
      willCustomerProvideSupplies,
      reimbuseWorkerExpenses,
      isWorkerNotAvailableForEmployees,
      areServicesKeyAspectForBusiness,
      hasPerformedSimilarServices,
      workerAdvertiseServices,
    } = values;

    const data: AssignmentQuestionnaireInfoModel = {
      ongoingTrainingRequired: mapQuestionnaireToRequest(requireWorkerTraining),
      customerControl: mapQuestionnaireToRequest(controlWorker),
      clockInOrOutDictated: mapQuestionnaireToRequest(dictateClockInOut),
      supervisionOfWorker: mapQuestionnaireToRequest(
        superviseCustomersEmployees,
      ),
      suppliesProvision: mapQuestionnaireToRequest(willCustomerProvideSupplies),
      businessExpensesReimbursed: mapQuestionnaireToRequest(
        reimbuseWorkerExpenses,
      ),
      specializedSkillsetEmployeeAvailbility: mapQuestionnaireToRequest(
        isWorkerNotAvailableForEmployees,
      ),
      servicesAreKeyAspectOfBM: mapQuestionnaireToRequest(
        areServicesKeyAspectForBusiness,
      ),
      workerPerformedSimilarServices: mapQuestionnaireToRequest(
        hasPerformedSimilarServices,
      ),
      servicesAdvertisedByWorker: mapQuestionnaireToRequest(
        workerAdvertiseServices,
      ),
    };

    const correctAction =
      isAdmin && isEditMode
        ? updateMemberQuestionnaire
        : saveMemberQuestionnaire;

    const result = await dispatch(
      correctAction({
        isAdmin,
        assignmentId,
        assignmentKey,
        quesitonnaireData: data,
      }),
    );

    if (correctAction.fulfilled.match(result)) {
      setPrimaryDetails(result.payload.primaryInfo);
      result.payload.additionalInfo &&
        setAdditionalDetails &&
        setAdditionalDetails(result.payload.additionalInfo);
      BaseAlert.success('Member questionnaire saved. ');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title={isEditMode ? 'Edit member questionnaire' : 'Member questionnaire'}
      open={true}
      closable={true}
      maxWidth={888}
      onCancel={handleCloseModal}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <QuestionnaireElement
              fieldPrefix="requireWorkerTraining"
              label="With the exception of initial project orientation, does the customer require the worker to undergo ongoing training?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="controlWorker"
              label="Does the customer control or have the right to control what the worker does and how the worker does his or her job?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="dictateClockInOut"
              label="Will the customer dictate when the worker should clock in or out?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="superviseCustomersEmployees"
              label="Will the worker supervise any of the customer's employees?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="willCustomerProvideSupplies"
              label="With the exception of equipment issued for the purpose of security, will the customer be providing supplies to the worker?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="reimbuseWorkerExpenses"
              label="Does the customer reimburse the worker for business expenses (office rent, commercial insurance, etc.)?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="isWorkerNotAvailableForEmployees"
              label="Is the worker performing a specialized skillset currently not available through their existing employees?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="areServicesKeyAspectForBusiness"
              label="Are the services performed a key aspect of the customer's regular business model?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="hasPerformedSimilarServices"
              label="Has the worker performed similar services to other clients previously?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="workerAdvertiseServices"
              label="Does the worker advertise their services?"
            />
          </div>

          <BaseModalFooter>
            <div>
              <Button
                data-id="closeMemberQuestionnaireModalBtn"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>

              <SubmitButton
                type="primary"
                data-id="submitMemberQuestionnaireBtn"
              >
                Save
              </SubmitButton>
            </div>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
